import { PiazzaInputContext } from 'ComponentLibrary/src/molecules/Form/PiazzaInputContext'
import { Autocomplete as PiazzaAutocomplete } from 'ComponentLibrary/src/piazza/forms'
import { Autocomplete, TextField } from '@mui/material'
import { XIcon } from '@phosphor-icons/react'
import { CaretDown } from 'ComponentLibrary/src/atoms/Select/Select'
import { CREATE_ORDER_VALUATION_REASON_GROUPED_OPTIONS, type ReasonForValuation } from 'ComponentLibrary/src/common/valuation-reason'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { type FocusEvent, ReactNode, useContext, useMemo } from 'react'

export type ValuationReasonAutocompleteOption = { value: ReasonForValuation; label: string; groupLabel: string }

const defaultValuationReasonAutocompleteOptions: ValuationReasonAutocompleteOption[] = Object.values(
    CREATE_ORDER_VALUATION_REASON_GROUPED_OPTIONS
).flatMap((group) =>
    [...group.options].sort((a, b) => a.label.localeCompare(b.label)).map((option) => ({ ...option, groupLabel: group.label }))
)

export type ValuationReasonAutocompleteProps = {
    label: string
    value: ReasonForValuation | '' | undefined
    onChange: (value: ReasonForValuation | '') => void
    error?: boolean
    helperText?: ReactNode
    disabled?: boolean
    fullWidth?: boolean
    disableClearable?: boolean
    openOnFocus?: boolean
    name?: string
    required?: boolean
    onBlur?: () => void
    options?: ValuationReasonAutocompleteOption[]
}

export const ValuationReasonAutocomplete = ({
    label,
    value,
    onChange,
    error,
    helperText,
    disabled = false,
    fullWidth = true,
    disableClearable = false,
    openOnFocus = true,
    name,
    required = false,
    onBlur,
    options = defaultValuationReasonAutocompleteOptions,
}: ValuationReasonAutocompleteProps) => {
    const usePiazzaInputs = useContext(PiazzaInputContext)
    const selectedOption: ValuationReasonAutocompleteOption | null = useMemo(() => {
        if (!value) {
            return null
        }
        return options.find((option) => option.value === value) ?? null
    }, [options, value])

    if (usePiazzaInputs) {
        return (
            <PiazzaAutocomplete
                label={label}
                name={name}
                required={required}
                options={options}
                value={value || undefined}
                onValueChange={(value) => onChange(value ?? '')}
                onBlur={onBlur}
                errorMessage={error ? String(helperText ?? '') : undefined}
                disabled={disabled}
                disableClearable={disableClearable}
                openOnFocus={openOnFocus}
                fullWidth={fullWidth}
                groupBy={(option) => options.find((entry) => entry.value === option.value)?.groupLabel ?? ''}
            />
        )
    }

    return (
        <Autocomplete
            options={options}
            value={selectedOption}
            onChange={(_, option) => {
                if (!option) {
                    if (disableClearable) {
                        return
                    }
                    onChange('')
                    return
                }
                onChange(option.value)
            }}
            openOnFocus={openOnFocus}
            disableClearable={disableClearable}
            disabled={disabled}
            isOptionEqualToValue={(option, valueOption) => option.value === valueOption.value}
            getOptionLabel={(option) => option.label}
            groupBy={(option) => option.groupLabel}
            renderInput={(params) => {
                const { inputProps, ...rest } = params
                return (
                    <TextField
                        {...rest}
                        inputProps={{
                            ...inputProps,
                            name,
                            onBlur: (event) => {
                                inputProps.onBlur?.(event as unknown as FocusEvent<HTMLInputElement>)
                                onBlur?.()
                            },
                        }}
                        sx={{ '& label': { paddingRight: tokens.SizingIconM } }}
                        label={label}
                        required={required}
                        error={error}
                        helperText={helperText}
                    />
                )
            }}
            popupIcon={<CaretDown />}
            clearIcon={<XIcon size={tokens.SizingIconS} weight="bold" />}
            fullWidth={fullWidth}
        />
    )
}
